import { useQuery } from "@apollo/client";

import NodesQuery from "lib/graphql/queries/NodesQuery";
import type { INodesQuery, INodesQueryVariables } from "lib/graphql/queries/__generated__/NodesQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";

type Node = INodesQuery["nodes"][0];
const cache = new Map<string, Node>();
const isServer = typeof window === "undefined";

export function useNodes(identifiers: string[]) {
  const fullMatch = identifiers.every((id) => cache.has(id));
  const missingObjectIds = identifiers.filter((id) => !cache.has(id));

  const { data, previousData, loading, ...rest } = useQuery<INodesQuery, INodesQueryVariables>(NodesQuery, {
    variables: {
      ids: missingObjectIds,
    },
  });

  const nodes = (loading && !data ? previousData?.nodes : data?.nodes) || [];

  // If no identifiers are provided, return an empty array
  if (identifiers.length === 0) {
    return {
      ...rest,
      loading: false,
      nodes: [],
    };
  }

  if (isServer) {
    return {
      ...rest,
      loading,
      nodes,
    };
  }

  if (fullMatch) {
    return {
      ...rest,
      loading: false,
      nodes: identifiers.map((id) => cache.get(id)),
    };
  }

  nodes.forEach((node) => {
    cache.set(node.id, node);
  });

  return {
    ...rest,
    loading,
    nodes: identifiers.map((candidate) => cache.get(candidate)).filter(notUndefinedOrNull),
  };
}

type UseNodesReturnType = ReturnType<typeof useNodes>;
type UseNodeReturnType = Omit<UseNodesReturnType, "nodes"> & { node: Node | null };
type UseSpecificNodeReturnType<SpecificNode extends Node> = UseNodeReturnType & { node: SpecificNode | null };

export function useNode(identifier: string | null): UseNodeReturnType {
  const { nodes, ...rest } = useNodes(identifier ? [identifier] : []);
  return {
    ...rest,
    node: nodes.length === 1 ? nodes[0] || null : null,
  };
}

export function useSpecificNode<
  Typename extends Node["__typename"],
  SpecificNode extends Extract<Node, { __typename: Typename }>,
>(identifier: string | null, resolveIf?: Typename): UseSpecificNodeReturnType<SpecificNode> {
  const { nodes, ...rest } = useNodes(identifier ? [identifier] : []);

  return {
    ...rest,
    node: nodes.length === 1 && nodes[0]?.__typename === resolveIf ? (nodes[0] as SpecificNode) : null,
  };
}
